import { ModelStatus } from '../../../../networked-shared-interfaces/constants/model-status';
import { ICommunity } from '../../../../networked-shared-interfaces/interfaces-phase2/platform-admin/platform-admin';
import { buildMockSteps } from './constants';
import { ISetupStep, StepStatus } from './types';

// ── ModelStatus → UI step status ───────────────────────────────────────────────

/**
 * Converts a raw ModelStatus number to the UI's StepStatus union.
 * Mapping is intentionally defensive: unknown values fall back to 'pending'.
 */
export const modelStatusToStepStatus = (status: ModelStatus | undefined): StepStatus => {
	if (status === undefined || status === null) return 'pending';

	switch (status) {
		// ── Completed states ──────────────────────────────────────────────────
		case ModelStatus.Active:
		case ModelStatus.Accepted:
			return 'completed';

		// ── In-progress / lifecycle states ────────────────────────────────────
		case ModelStatus.Waiting:
		case ModelStatus.Pending:
		case ModelStatus.SupabaseCustomDomainPending:
		case ModelStatus.SupabaseCustomDomainVerificationPending:
		case ModelStatus.SupabaseCustomDomainActivationPending:
			return 'in_progress';

		// ── Error / blocked states ────────────────────────────────────────────
		case ModelStatus.Declined:
		case ModelStatus.Blocked:
		case ModelStatus.BlockedDeleted:
		case ModelStatus.Deleted:
		case ModelStatus.Restricted:
			return 'error';

		// ── Everything else (not started / unknown) ───────────────────────────
		default:
			return 'pending';
	}
};

/**
 * Normalizes API values that may arrive as string enums from JSON.
 */
const normalizeModelStatus = (raw: ModelStatus | string | number | undefined | null): ModelStatus | undefined => {
	if (raw === undefined || raw === null) return undefined;
	if (typeof raw === 'number') return raw as ModelStatus;
	const n = Number(raw);
	return Number.isNaN(n) ? undefined : (n as ModelStatus);
};

/**
 * True when custom domain is in the same "done" state as the Custom Domain stepper node
 * (Active or Accepted). Unlocks Supabase and OneSignal.
 *
 * Uses the same mapping as `modelStatusToStepStatus`, not a raw `=== Active` check, so we
 * stay consistent if the backend adds more "complete" codes. Also coerces string statuses
 * from JSON so `"35"` does not stay blocked while the UI shows COMPLETED.
 */
const isCustomDomainComplete = (community: ICommunity): boolean => {
	const status = normalizeModelStatus(community.customDomain?.status as ModelStatus | string | number | undefined);
	return modelStatusToStepStatus(status) === 'completed';
};

/**
 * Returns true when Branch credentials are considered fully configured:
 * at minimum appId and branch_key must be present.
 */
const isBranchConfigured = (community: ICommunity): boolean =>
	!!(community.branchDetails?.appId && community.branchDetails?.branch_key);

/**
 * Derives ISetupStep[] from a live ICommunity payload, overlaying API-driven
 * statuses onto the static baseline steps from constants.ts.
 */
export const mapCommunityToSteps = (community: ICommunity | null): ISetupStep[] => {
	const baseline = buildMockSteps();

	if (!community) return baseline;

	const domainComplete = isCustomDomainComplete(community);

	return baseline.map((step): ISetupStep => {
		switch (step.id) {
			case 'branch': {
				const configured = isBranchConfigured(community);
				return {
					...step,
					status: configured ? 'completed' : 'pending',
					sublabel: configured
						? `App ID: ${community.branchDetails!.appId}`
						: 'Credentials not yet configured',
				};
			}

			case 'custom_domain': {
				const rawDomainStatus = normalizeModelStatus(
					community.customDomain?.status as ModelStatus | string | number | undefined
				);
				const status = modelStatusToStepStatus(rawDomainStatus);
				const domain = community.customDomain?.domain;
				return {
					...step,
					status,
					sublabel: domain ? `Domain: ${domain}` : step.sublabel,
				};
			}

			case 'supabase':
				if (!domainComplete) return { ...step, status: 'blocked' };
				return {
					...step,
					status: modelStatusToStepStatus(
						normalizeModelStatus(community.supabaseStatus as ModelStatus | string | number | undefined)
					),
				};

			case 'onesignal':
				if (!domainComplete) return { ...step, status: 'blocked' };
				return {
					...step,
					status: modelStatusToStepStatus(
						normalizeModelStatus(community.oneSignalStatus as ModelStatus | string | number | undefined)
					),
				};

			case 'posthog_analytics': {
				const posthogApiKey = community.posthogApiKey?.trim();
				return {
					...step,
					status: posthogApiKey ? 'completed' : 'pending',
					sublabel: posthogApiKey ? `API Key: ${posthogApiKey}` : 'API key not yet configured',
				};
			}

			default:
				return step;
		}
	});
};
